import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CommonButton from '../../../Components/CommonButton';
import CommonDropdown from '../../../Components/CommonDropdown';
import CommonLoader from '../../../Components/CommonLoader';
import CommonModal from '../../../Components/CommonModal';
import CommonStepper from '../../../Components/CommonStepper';
import ModuleIcon from '../../../Components/ModuleIcon';
import { useToast } from '../../../Components/CommonToast';
import { useConfirm } from '../../../Components/ConfirmModal';
import { heading3, bodySmall, label as labelPreset, fontWeight, Body } from '../../../Constants/Fonts';
import {
    risk_portal_workflow_config,
    risk_portal_update_workflow,
    risk_portal_seed_defaults,
    risk_portal_get_settings,
    risk_portal_update_settings,
} from '../../../services';

// Unwrap the AIGApi / SbomApiIntercepter envelope — same helper the sibling
// pages use.
const unwrap = (res) =>
    res?.data?.response_data?.data ?? res?.data?.data ??
    res?.statusCode?.response_data?.data ?? res?.statusCode?.data ?? null;

// A deleted stage that still holds risks makes the save come back 409 with a
// `stages_in_use` list ([{ id, name, risk_count }]). Pull it out across the
// envelope-nesting levels the body might arrive in.
const stagesInUseOf = (body) => {
    const v = body?.stages_in_use ?? body?.error?.stages_in_use
        ?? body?.response_data?.stages_in_use ?? body?.response_data?.data?.stages_in_use
        ?? body?.data?.stages_in_use;
    return Array.isArray(v) && v.length ? v : null;
};
// After a successful move+save the body carries `moved_risks` ({ "<removed id>": N }).
const movedRisksOf = (body) =>
    body?.moved_risks ?? body?.response_data?.moved_risks
    ?? body?.response_data?.data?.moved_risks ?? body?.data?.moved_risks ?? null;

// HARD validation failures arrive as a non-empty `errors` array (distinct from the
// advisory `warnings` array). These can't be forced past — the save is rejected
// outright — so they must be surfaced verbatim, not auto-retried with force:true.
const hardErrorsOf = (body) => {
    const v = Array.isArray(body?.errors) ? body.errors
        : Array.isArray(body?.error?.errors) ? body.error.errors : null;
    return v && v.length ? v : null;
};

// ── Static reference data ──────────────────────────────────────────
// Built-in role display labels; custom roles (extra keys in role_config) are
// humanised on the fly.
const BUILTIN_ROLE_LABELS = {
    risk_admin: 'Risk Admin', risk_manager: 'Risk Manager', risk_owner: 'Risk Owner',
};
const humanize = (k = '') => String(k).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();

const STAGE_TYPES = ['form', 'assessment', 'treatment', 'review', 'approval', 'monitoring', 'closed'];
const PERM_KEYS = ['can_configure', 'can_assess', 'can_treat', 'can_approve'];
const ROUTABLE_ROLES = ['risk_manager', 'risk_owner', 'risk_admin'];
// The only roles selectable as a stage owner in the workflow editor.
const OWNER_ROLES = ['risk_admin', 'risk_manager', 'risk_owner'];
const REQUIRED_ARTIFACTS = [
    { key: 'assessment', label: 'Assessment' },
    { key: 'treatment_plan', label: 'Treatment Plan' },
    { key: 'verified_treatment', label: 'Verified Plan' },
    { key: 'evidence', label: 'Evidence' },
    { key: 'kri', label: 'KRI' },
    { key: 'controls', label: 'Controls' },
    { key: 'owner', label: 'Owner Set' },
    { key: 'acknowledgement', label: 'Owner Ack' },
];
const STATE_KEYS = ['open', 'in_progress', 'accepted', 'closed', 'transferred'];
const STATE_PLACEHOLDERS = { open: 'Open', in_progress: 'In Progress', accepted: 'Accepted', closed: 'Closed', transferred: 'Transferred' };
const SEVERITY_DEFAULT_COLORS = { critical: 'var(--color-critical)', high: 'var(--color-high)', medium: 'var(--color-medium)', low: 'var(--color-low)' };
const SEVERITY_HEX_DEFAULT = { critical: '#b91c1c', high: '#ea580c', medium: '#eab308', low: '#16a34a' };

// Fallback 7-stage ISO workflow when the org has no saved config yet.
const DEFAULT_STAGES = [
    { id: 'identification', name: 'Identification', order: 1, stage_type: 'form', next_stages: ['assessment'], sla_days: 3 },
    { id: 'assessment', name: 'Assessment', order: 2, stage_type: 'assessment', next_stages: ['treatment'], sla_days: 5 },
    { id: 'treatment', name: 'Treatment Planning', order: 3, stage_type: 'treatment', next_stages: ['review'], sla_days: 10 },
    { id: 'review', name: 'Review', order: 4, stage_type: 'review', next_stages: ['acceptance'], sla_days: 5 },
    { id: 'acceptance', name: 'Acceptance', order: 5, stage_type: 'approval', next_stages: ['monitoring'], sla_days: 5 },
    { id: 'monitoring', name: 'Monitoring', order: 6, stage_type: 'monitoring',  next_stages: ['closed'], sla_days: null },
    { id: 'closed', name: 'Closed', order: 7, stage_type: 'closed', next_stages: [], sla_days: null },
];

// Shared inline field style — token-driven, matches CommonModal inputs.
const fieldStyle = {
    width: '100%', padding: '7px 10px', fontSize: 13,
    border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)',
    background: 'var(--color-surface)', color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family)', outline: 'none',
};
const SECTION_TITLE = labelPreset({
    color: 'var(--color-text-secondary)', fontWeight: fontWeight.bold,
    textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0,
});

// Small pill toggle used for required-artifacts and SoD stages.
function TogglePill({ on, disabled, onClick, children }) {
    return (
        <button
            type="button" disabled={disabled} onClick={onClick}
            style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 12, fontFamily: 'var(--font-family)',
                cursor: disabled ? 'default' : 'pointer',
                border: `1px solid ${on ? 'var(--color-primary)' : 'var(--color-border-strong)'}`,
                background: on ? 'var(--color-primary-soft)' : 'var(--color-surface)',
                color: on ? 'var(--color-primary)' : 'var(--color-text-muted)',
                fontWeight: on ? fontWeight.semibold : fontWeight.regular,
                transition: 'all .15s ease',
            }}
        >
            {on ? '✓ ' : ''}{children}
        </button>
    );
}

export default function Workflow({ canManage = false }) {
    const [wf, setWf] = useState({ stages: DEFAULT_STAGES, role_config: {} });
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingCust, setSavingCust] = useState(false);
    const [panel, setPanel] = useState(null); // null | 'roles' | 'customisation'
    // Set when a save is blocked because deleted stages still hold risks:
    // { list: [{ id, name, risk_count }], targets: { <removed id>: <target stage id> } }.
    const [movePrompt, setMovePrompt] = useState(null);

    const { notify, toastNode } = useToast();
    const { confirm, confirmModal } = useConfirm();

    const load = useCallback(async () => {
        const [cfg, st] = (await Promise.allSettled([
            risk_portal_workflow_config(),
            risk_portal_get_settings(),
        ])).map((r) => (r.status === 'fulfilled' ? unwrap(r.value) : null));

        const stages = Array.isArray(cfg?.stages) && cfg.stages.length
            ? [...cfg.stages].sort((a, b) => (a.order || 0) - (b.order || 0))
            : DEFAULT_STAGES;
        setWf({ stages, role_config: cfg?.role_config || {} });
        setSettings(st || {});
        setLoading(false);
    }, []);

    useEffect(() => { setLoading(true); load(); }, [load]);

    const stages = wf.stages || [];

    // Role label map: built-ins overlaid with org labels + any custom role keys.
    const roleLabels = useMemo(() => {
        const custom = settings.role_labels || {};
        const keys = new Set([...Object.keys(BUILTIN_ROLE_LABELS), ...Object.keys(wf.role_config || {})].filter((k) => k && !k.startsWith('_')));
        const out = {};
        keys.forEach((k) => { out[k] = (custom[k] && custom[k].trim()) || BUILTIN_ROLE_LABELS[k] || humanize(k); });
        return out;
    }, [settings.role_labels, wf.role_config]);

    // A stage owner can only be one of these three roles (plus "no owner").
    // Other roles that exist in role_config (ciso, reviewer, business owner…)
    // are intentionally not offered here.
    const ownerOptions = useMemo(
        () => [
            { value: '', label: 'System / no owner' },
            ...OWNER_ROLES.map((k) => ({ value: k, label: roleLabels[k] || BUILTIN_ROLE_LABELS[k] || humanize(k) })),
        ],
        [roleLabels]
    );

    // ── Stage editing ──────────────────────────────────────────────
    const updateStage = (id, field, raw) => {
        setWf((prev) => ({
            ...prev,
            stages: (prev.stages || []).map((s) => {
                if (s.id !== id) return s;
                let value = raw;
                if (field === 'sla_days') {
                    const n = parseInt(raw, 10);
                    value = raw === '' || raw === null ? null : (Number.isFinite(n) && n > 0 ? n : null);
                }
                return { ...s, [field]: value };
            }),
        }));
    };

    const moveStage = (id, dir) => {
        setWf((prev) => {
            const list = [...(prev.stages || [])];
            const i = list.findIndex((s) => s.id === id);
            const j = i + dir;
            if (i < 0 || j < 0 || j >= list.length) return prev;
            [list[i], list[j]] = [list[j], list[i]];
            list.forEach((s, idx) => { s.order = idx + 1; });
            return { ...prev, stages: list };
        });
    };

    const addStage = () => {
        const newId = `stage_${Math.random().toString(36).slice(2, 8)}`;
        setWf((prev) => ({
            ...prev,
            stages: [...(prev.stages || []), {
                id: newId, name: 'New Stage', order: (prev.stages?.length || 0) + 1,
                stage_type: 'form', owner_role: 'risk_manager', next_stages: [], sla_days: 5, required_artifacts: [],
            }],
        }));
    };

    const deleteStage = async (id) => {
        const target = stages.find((s) => s.id === id);
        if (!target) return;
        const ok = await confirm({
            title: 'Delete stage',
            message: `Delete stage "${target.name}"? Stages pointing to it will be rewired to its next stage so the pipeline stays connected.`,
            confirmLabel: 'Delete', danger: true, themeColor: false,
        });
        if (!ok) return;
        // Bridge the gap: any stage that pointed AT the deleted one inherits the
        // deleted stage's own next_stages. Rerouting to End instead would turn
        // those predecessors into terminal stages of the wrong type, which the
        // backend rejects ("terminal stage has type 'review' — change to 'closed'").
        const bridge = (target.next_stages || []).filter((n) => n !== id);
        const remaining = (wf.stages || []).filter((s) => s.id !== id).map((s) => {
            if (!(s.next_stages || []).includes(id)) return s;
            const next = (s.next_stages || []).filter((n) => n !== id);
            bridge.forEach((b) => { if (!next.includes(b)) next.push(b); });
            return { ...s, next_stages: next };
        });
        // Optimistically drop it, then persist in the same action — pass `remaining`
        // as an override because setWf is async and runSave would otherwise POST the
        // stale list. If the save is rejected (or errors), reload so the removed
        // stage reappears instead of lingering as a phantom local-only deletion.
        setWf((prev) => ({ ...prev, stages: remaining }));
        const outcome = await runSave(null, remaining);
        if (outcome === 'error') await load();
    };

    const toggleArtifact = (id, key) => {
        const stage = stages.find((s) => s.id === id);
        const list = Array.isArray(stage?.required_artifacts) ? stage.required_artifacts : [];
        updateStage(id, 'required_artifacts', list.includes(key) ? list.filter((x) => x !== key) : [...list, key]);
    };

    // ── Persistence ────────────────────────────────────────────────
    // POST the config; `force` re-submits past non-blocking warnings, and
    // `moveRisksTo` ({ <removed id>: <target id> }) tells the backend where to
    // relocate risks that still sit on a deleted stage. Always sends the FULL
    // role_config — the backend replaces the saved value with whatever we send,
    // so a partial object would wipe the org's roles/settings. Returns the
    // unwrapped response envelope (statusCode body) so the caller can spot the
    // soft-validation prompt (requires_confirmation) and the 409 (stages_in_use).
    const postWorkflow = async (force, moveRisksTo, stagesOverride) => {
        const params = { stages: stagesOverride || wf.stages, role_config: wf.role_config };
        if (moveRisksTo && Object.keys(moveRisksTo).length) params.move_risks_to = moveRisksTo;
        try {
            const res = await risk_portal_update_workflow(params, force);
            // Resolved path: SbomApiIntercepter wraps a 2xx body as { statusCode }.
            return res?.statusCode || res?.data || {};
        } catch (e) {
            // Rejected path: the soft-validation gate (4xx) and the stages-in-use
            // gate (409) both carry their envelope on the axios error response
            // body. Re-surface it so the caller can inspect it instead of
            // treating it as a hard failure.
            const body = e?.response?.data;
            if (body && typeof body === 'object') return body;
            throw e;   // genuine network / unexpected error
        }
    };

    // Run one save attempt (optionally carrying a move_risks_to map) and classify
    // the outcome: 'in_use' (needs the move prompt), 'error', or 'ok'.
    const attemptSave = async (moveRisksTo, stagesOverride) => {
        let body = await postWorkflow(false, moveRisksTo, stagesOverride);
        let inUse = stagesInUseOf(body);
        if (inUse) return { status: 'in_use', list: inUse };
        // Hard validation errors (non-empty `errors`) block the save and can't be
        // forced past — surface them verbatim so the user knows exactly what to
        // fix (e.g. "Terminal stage 'Review' has type 'review' — change it to
        // 'closed'"). Checked BEFORE the soft-warning path so we don't waste a
        // force:true retry that will fail identically.
        let hard = hardErrorsOf(body);
        if (hard) return { status: 'error', message: hard.join(' ') };
        // Soft validation: structurally valid but the backend flags NON-BLOCKING
        // warnings (e.g. a review stage with no reject-back path) and returns
        // status:0 + requires_confirmation instead of persisting. Advisory only —
        // auto re-submit with force:true and surface the warnings as a notice.
        const warnings = Array.isArray(body.warnings) ? body.warnings : [];
        const needsForce = body.requires_confirmation || (body.status === 0 && warnings.length > 0);
        if (needsForce) {
            body = await postWorkflow(true, moveRisksTo, stagesOverride);
            // force:true does NOT skip the stages-in-use check — it can still 409.
            inUse = stagesInUseOf(body);
            if (inUse) return { status: 'in_use', list: inUse };
            hard = hardErrorsOf(body);
            if (hard) return { status: 'error', message: hard.join(' ') };
            if (body.requires_confirmation) return { status: 'error', message: body.error?.message || 'Could not save — the workflow still needs confirmation.' };
            if (body.status === 0 && body.error?.message) return { status: 'error', message: body.error.message };
            return { status: 'ok', body, warnings };
        }
        if (body.status === 0 && body.error?.message) return { status: 'error', message: body.error.message };
        return { status: 'ok', body, warnings: [] };
    };

    // "N risks moved to X" summary from a successful move+save response.
    const movedMessage = (body, moveRisksTo) => {
        const moved = movedRisksOf(body);
        if (!moved || !moveRisksTo) return null;
        const parts = Object.entries(moved).filter(([, n]) => Number(n) > 0).map(([removedId, n]) => {
            const targetName = stages.find((s) => s.id === moveRisksTo[removedId])?.name || moveRisksTo[removedId];
            return `${n} risk${Number(n) === 1 ? '' : 's'} moved to ${targetName}`;
        });
        return parts.length ? parts.join('; ') : null;
    };

    const runSave = async (moveRisksTo, stagesOverride) => {
        setSaving(true);
        try {
            const r = await attemptSave(moveRisksTo, stagesOverride);
            if (r.status === 'in_use') {
                // Open the move prompt, pre-selecting the first remaining stage as
                // the default target for each deleted stage that still has risks.
                const firstTarget = stages[0]?.id || '';
                const targets = {};
                r.list.forEach((x) => { targets[x.id] = moveRisksTo?.[x.id] || firstTarget; });
                setMovePrompt({ list: r.list, targets });
                return 'in_use';
            }
            if (r.status === 'error') { notify(r.message, 'error'); return 'error'; }
            const moved = movedMessage(r.body, moveRisksTo);
            const warnings = r.warnings || [];
            notify(
                moved
                    ? `Workflow saved. ${moved}.`
                    : warnings.length
                        ? `Workflow saved. ${warnings.length} advisory warning${warnings.length === 1 ? '' : 's'}: ${warnings[0]}`
                        : 'Workflow configuration saved',
                warnings.length ? 'warning' : 'success'
            );
            setMovePrompt(null);
            setPanel(null);
            await load();
            return 'ok';
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Failed to save workflow', 'error');
            return 'error';
        } finally {
            setSaving(false);
        }
    };

    // Buttons pass a click event — ignore it; the initial save never moves risks.
    const saveWorkflow = () => runSave(null);
    const moveTargetsComplete = !!movePrompt && movePrompt.list.every((x) => movePrompt.targets[x.id]);

    const seedDefaults = async () => {
        const ok = await confirm({
            title: 'Seed default data',
            message: 'Create the default categories, appetites and workflow for this org if they do not exist yet? Existing data is left untouched.',
            confirmLabel: 'Seed', themeColor: false,
        });
        if (!ok) return;
        setSaving(true);
        try {
            const d = unwrap(await risk_portal_seed_defaults());
            notify(`Seeded ${d?.categories_created || 0} categories, ${d?.appetites_created || 0} appetites; workflow ${d?.workflow_created ? 'created' : 'already existed'}`, 'success');
            await load();
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Seed failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    const saveCustomisation = async () => {
        setSavingCust(true);
        try {
            await risk_portal_update_settings({
                severity_thresholds: settings.severity_thresholds || [4, 9, 15],
                scale_max: settings.scale_max || 5,
                share_token_ttl_days: settings.share_token_ttl_days ?? 30,
                email_branding: settings.email_branding || {},
                sod_stages: settings.sod_stages || ['review'],
                type_routing: settings.type_routing || {},
                role_labels: settings.role_labels || {},
                state_labels: settings.state_labels || {},
                rating_colors: settings.rating_colors || {},
            });
            notify('Customisation saved', 'success');
            setPanel(null);
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Failed to save customisation', 'error');
        } finally {
            setSavingCust(false);
        }
    };

    // Optimistic settings patch (persisted on Save Customisation).
    const patchSettings = (patch) => setSettings((prev) => ({ ...prev, ...patch }));

    if (loading) return <CommonLoader fullPage label="Loading workflow…" />;

    return (
        <div className="rp1_widget">
            {/* Header + primary actions */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                <div>
                    <h2 style={heading3({ margin: 0 })}>Workflow Configuration</h2>
                    <p style={bodySmall({ color: 'var(--color-text-muted)', margin: '4px 0 0', maxWidth: 720 })}>
                        Customise the entire risk lifecycle — rename stages, change owner roles, edit SLAs, reroute
                        transitions, and add or remove stages. Changes apply to new transitions; risks already in a stage
                        keep their existing deadlines.
                    </p>
                </div>
                {canManage && (
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <CommonButton variant="secondary" text="Seed Default Data" onClick={seedDefaults} disable={saving} />
                        <CommonButton text={saving ? 'Saving…' : 'Save Workflow'} onClick={saveWorkflow} disable={saving} />
                    </div>
                )}
            </div>

            {!canManage && (
                <p style={bodySmall({ color: 'var(--color-text-muted)', margin: '10px 0 0' })}>
                    Read-only. Only a Risk Admin can edit the workflow configuration.
                </p>
            )}

            {/* Buttons that open the two secondary sections (never inline) */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '16px 0 8px' }}>
                <CommonButton text="Role Responsibilities" onClick={() => setPanel('roles')} />
                <CommonButton text="Customisation" onClick={() => setPanel('customisation')} />
            </div>

            {/* Risk lifecycle pipeline — neutral numbered overview */}
            <section className='rp1_widget' style={{ marginTop: 18 }}>
                <h3 style={{ ...SECTION_TITLE, marginBottom: 16 }}>Risk Lifecycle Pipeline</h3>
                <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
                    <CommonStepper steps={stages.map((s) => ({ id: s.id, label: s.name }))} currentIndex={-1} />
                </div>
            </section>

            {/* Stage configuration — editable cards */}
            <section style={{ marginTop: 26 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
                    <h3 style={SECTION_TITLE}>Stage Configuration</h3>
                    {canManage && <CommonButton text="+ Add Stage" onClick={addStage} />}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14, alignItems: 'start' }}>
                    {stages.map((s, i) => (
                        <StageCard
                            key={s.id}
                            stage={s}
                            index={i}
                            total={stages.length}
                            canManage={canManage}
                            stages={stages}
                            ownerOptions={ownerOptions}
                            roleLabels={roleLabels}
                            onUpdate={updateStage}
                            onMove={moveStage}
                            onDelete={deleteStage}
                            onToggleArtifact={toggleArtifact}
                        />
                    ))}
                </div>
            </section>

            {/* ── Role Responsibilities (opens on button) ── */}
            <CommonModal
                show={panel === 'roles'}
                onHide={() => setPanel(null)}
                title="Role Responsibilities"
                subtitle={canManage ? 'Click any permission to toggle. Save to persist.' : 'Read-only view of role capabilities.'}
                mid
                buttons={canManage ? [
                    { text: 'Close', variant: 'secondary', onClick: () => setPanel(null), disabled: saving },
                    { text: saving ? 'Saving…' : 'Save', variant: 'primary', onClick: saveWorkflow, disabled: saving },
                ] : [{ text: 'Close', variant: 'secondary', onClick: () => setPanel(null) }]}
            >
                <RoleResponsibilities
                    roleLabels={roleLabels}
                    stages={stages}
                    roleConfig={wf.role_config}
                    canManage={canManage}
                    onToggle={(roleKey, permKey) => setWf((prev) => {
                        const rc = { ...(prev.role_config || {}) };
                        const cur = rc[roleKey]?.permissions || {};
                        rc[roleKey] = { ...(rc[roleKey] || {}), permissions: { ...cur, [permKey]: !cur[permKey] } };
                        return { ...prev, role_config: rc };
                    })}
                />
            </CommonModal>

            {/* ── Customisation (opens on button) ── */}
            <CommonModal
                show={panel === 'customisation'}
                onHide={() => setPanel(null)}
                title="Customisation"
                subtitle={canManage ? 'Per-org overrides for scoring, branding, routing and labels.' : 'Read-only view of per-org settings.'}
                mid
                buttons={canManage ? [
                    { text: 'Close', variant: 'secondary', onClick: () => setPanel(null), disabled: savingCust },
                    { text: savingCust ? 'Saving…' : 'Save Customisation', variant: 'primary', onClick: saveCustomisation, disabled: savingCust },
                ] : [{ text: 'Close', variant: 'secondary', onClick: () => setPanel(null) }]}
            >
                <Customisation settings={settings} stages={stages} canManage={canManage} onPatch={patchSettings} />
            </CommonModal>

            {/* ── Move risks before deleting (opens on a 409 stages_in_use) ── */}
            <CommonModal
                show={!!movePrompt}
                onHide={() => { if (!saving) setMovePrompt(null); }}
                title="Move risks before deleting"
                subtitle="Some deleted stages still hold risks. Choose where each stage's risks should move, then save."
                mid
                buttons={[
                    { text: 'Cancel', variant: 'secondary', onClick: () => setMovePrompt(null), disabled: saving },
                    { text: saving ? 'Moving…' : 'Move & Save', variant: 'primary', onClick: () => runSave(movePrompt.targets), disabled: saving || !moveTargetsComplete },
                ]}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {(movePrompt?.list || []).map((x) => {
                        const count = x.risk_count ?? x.risks ?? x.count ?? 0;
                        return (
                            <div key={x.id} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md, 10px)', background: 'var(--color-surface)', padding: 14 }}>
                                <Body as="p" variant={2} color="var(--color-text-primary)" style={{ margin: '0 0 8px' }}>
                                    <strong>{x.name || x.id}</strong> has {count} risk{Number(count) === 1 ? '' : 's'}. Move {Number(count) === 1 ? 'it' : 'them'} to:
                                </Body>
                                <CommonDropdown
                                    value={movePrompt.targets[x.id] || ''}
                                    onChange={(v) => setMovePrompt((p) => ({ ...p, targets: { ...p.targets, [x.id]: v } }))}
                                    options={stages.map((s) => ({ value: s.id, label: s.name || s.id }))}
                                    minWidth={0} style={{ width: '100%' }}
                                />
                            </div>
                        );
                    })}
                </div>
            </CommonModal>

            {(saving || savingCust) && <CommonLoader fullPage label="Working…" />}
            {confirmModal}
            {toastNode}
        </div>
    );
}

// ── Stage card ─────────────────────────────────────────────────────
const FIELD_LABEL = labelPreset({ color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', margin: 0 });

// A hairline divider between card rows.
const Divider = () => <div style={{ height: 1, background: 'var(--color-border)' }} />;

// Reorder arrow — bordered rounded box (mockup up/down controls).
function ReorderBtn({ label, disabled, onClick, dir }) {
    return (
        <button type="button" title={label} aria-label={label} disabled={disabled} onClick={onClick}
            style={{
                width: 26, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border-strong)',
                background: 'var(--color-surface)', cursor: disabled ? 'not-allowed' : 'pointer',
                color: disabled ? 'var(--color-border-strong)' : 'var(--color-text-secondary)',
                opacity: disabled ? 0.5 : 1, padding: 0,
            }}>
            <ModuleIcon name={dir === 'down' ? 'chevron_down' : 'chevron_up'} size={12} color="currentColor" />
        </button>
    );
}

// Checkable capsule with a leading circle-check — on = green, off = grey ring.
function CheckPill({ on, disabled, onClick, children }) {
    return (
        <button type="button" disabled={disabled} onClick={onClick}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999,
                fontFamily: 'var(--font-family)', fontSize: 12, cursor: disabled ? 'default' : 'pointer',
                border: `1px solid ${on ? 'var(--color-success)' : 'var(--color-border-strong)'}`,
                background: on ? 'var(--color-success-soft)' : 'var(--color-surface)',
                color: on ? 'var(--color-success)' : 'var(--color-text-secondary)',
                fontWeight: on ? fontWeight.semibold : fontWeight.medium,
                transition: 'all .15s ease',
            }}>
            <Body as="span" style={{ color: on ? 'var(--color-success)' : 'var(--color-border-strong)', display: 'flex' }}>
                <ModuleIcon name={on ? 'check_circle' : 'circle'} size={14} color="currentColor" />
            </Body>
            {children}
        </button>
    );
}

function StageCard({ stage: s, index: i, total, canManage, stages, ownerOptions, roleLabels, onUpdate, onMove, onDelete, onToggleArtifact }) {
    const artifacts = Array.isArray(s.required_artifacts) ? s.required_artifacts : [];
    const nextValue = (s.next_stages || [])[0] || '';
    const nameStyle = { ...heading3({ margin: 0, color: 'var(--color-text-primary)' }), fontSize: 16 };

    return (
        <div style={{
            border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg, 14px)',
            background: 'var(--color-surface)', boxShadow: 'var(--shadow-sm, 0 1px 2px rgba(16,24,40,0.05))',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
            {/* Header — themed navy-tint strip: eyebrow + name + reorder arrows */}
            <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '12px 14px', background: 'var(--color-primary-soft)',
                borderBottom: '1px solid var(--color-border)',
            }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...labelPreset({ color: 'var(--color-primary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }), fontWeight: fontWeight.bold, marginBottom: 3 }}>
                        Stage {i + 1}
                    </div>
                    {canManage ? (
                        <input type="text" value={s.name || ''} onChange={(e) => onUpdate(s.id, 'name', e.target.value)}
                            aria-label="Stage name" title="Click to rename"
                            style={{ ...nameStyle, color: 'var(--color-primary)', width: '100%', border: '1px solid transparent', background: 'transparent', outline: 'none', padding: '1px 4px', margin: '0 0 0 -4px', borderRadius: 'var(--radius-sm)' }} />
                    ) : (
                        <div style={{ ...nameStyle, color: 'var(--color-primary)' }}>{s.name}</div>
                    )}
                </div>
                {canManage && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                        <ReorderBtn label="Move up" dir="up" disabled={i === 0} onClick={() => onMove(s.id, -1)} />
                        <ReorderBtn label="Move down" dir="down" disabled={i === total - 1} onClick={() => onMove(s.id, 1)} />
                    </div>
                )}
            </div>

            {/* Body */}
            <div style={{ padding: '12px 14px 14px' }}>
            {/* Stage type + Owner dropdowns, side by side in one row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {canManage ? (
                        <CommonDropdown value={s.stage_type || 'form'} onChange={(v) => onUpdate(s.id, 'stage_type', v)}
                            options={STAGE_TYPES.map((t) => ({ value: t, label: t[0].toUpperCase() + t.slice(1) }))} minWidth={0} style={{ width: '100%' }} />
                    ) : (
                        <Body as="span" variant={2} color="var(--color-text-primary)">{s.stage_type}</Body>
                    )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {canManage ? (
                        <CommonDropdown value={s.owner_role || ''} onChange={(v) => onUpdate(s.id, 'owner_role', v || null)}
                            options={ownerOptions} minWidth={0} style={{ width: '100%' }} />
                    ) : (
                        <Body as="span" variant={2} color="var(--color-text-primary)">{roleLabels[s.owner_role] || 'System'}</Body>
                    )}
                </div>
            </div>

            <div style={{ marginTop: 10 }}><Divider /></div>

            {/* SLA + Next, side by side in one row — label stacked above each control */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '9px 0' }}>
                <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Body as="span" variant={2} weight="medium" style={FIELD_LABEL}>SLA</Body>
                    {canManage ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Body as="span" variant={2} color="var(--color-text-muted)">Due within</Body>
                            <input type="number" min="0" placeholder="—" value={s.sla_days ?? ''}
                                onChange={(e) => onUpdate(s.id, 'sla_days', e.target.value)}
                                style={{
                                    width: 46, textAlign: 'center', padding: '5px 6px', fontSize: 13, fontWeight: fontWeight.bold,
                                    color: 'var(--color-medium)', background: 'var(--color-medium-soft)',
                                    border: '1px solid var(--color-medium-soft)', borderRadius: 'var(--radius-sm)',
                                    fontFamily: 'var(--font-family)', outline: 'none',
                                }} />
                            <Body as="span" variant={2} color="var(--color-text-muted)">days</Body>
                        </div>
                    ) : (
                        <Body as="span" variant={2} color="var(--color-text-primary)">{s.sla_days ? `${s.sla_days} days` : '—'}</Body>
                    )}
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Body as="span" variant={2} weight="medium" style={FIELD_LABEL}>Next</Body>
                    {canManage ? (
                        <CommonDropdown value={nextValue} onChange={(v) => onUpdate(s.id, 'next_stages', v ? [v] : [])}
                            options={[{ value: '', label: 'End (terminal)' }, ...stages.filter((x) => x.id !== s.id).map((x) => ({ value: x.id, label: x.name || x.id }))]}
                            minWidth={0} style={{ width: '100%' }} />
                    ) : (
                        <Body as="span" variant={2} color="var(--color-text-primary)">
                            {nextValue ? (stages.find((x) => x.id === nextValue)?.name || nextValue) : 'End'}
                        </Body>
                    )}
                </div>
            </div>
            <Divider />

            {/* Required to advance */}
            <div style={{ paddingTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                    <Body as="span" variant={2} weight="medium" style={FIELD_LABEL} title="What must be present before a risk can advance INTO this stage.">Required to Advance</Body>
                    <Body as="span" style={{
                        padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: fontWeight.semibold,
                        background: 'var(--color-success-soft)', color: 'var(--color-success)', flexShrink: 0,
                    }}>
                        {artifacts.length} / {REQUIRED_ARTIFACTS.length}
                    </Body>
                </div>
                {canManage ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {REQUIRED_ARTIFACTS.map((a) => (
                            <CheckPill key={a.key} on={artifacts.includes(a.key)} onClick={() => onToggleArtifact(s.id, a.key)}>{a.label}</CheckPill>
                        ))}
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {REQUIRED_ARTIFACTS.filter((a) => artifacts.includes(a.key)).map((a) => (
                            <CheckPill key={a.key} on disabled>{a.label}</CheckPill>
                        ))}
                        {artifacts.length === 0 && <Body as="span" variant={2} color="var(--color-text-muted)"><em>by stage type</em></Body>}
                    </div>
                )}
            </div>

            {/* Delete */}
            {canManage && (
                <div style={{display: 'flex', justifyContent:'end'}}>
                    <CommonButton variant="danger" onClick={() => onDelete(s.id)} text='Delete Stage' style={{ marginTop: 12, padding: '6px 10px', display: 'flex', alignItems: 'center' }} />
                </div>
            )}
            </div>
        </div>
    );
}

// ── Role Responsibilities matrix ───────────────────────────────────
function RoleResponsibilities({ roleLabels, stages, roleConfig, canManage, onToggle }) {
    const th = labelPreset({ color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 });
    const cellStyle = { padding: '12px 10px', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top', textAlign: 'left' };
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                <thead>
                    <tr>
                        <th style={{ ...cellStyle, ...th }}>Role</th>
                        <th style={{ ...cellStyle, ...th }}>Stages Owned</th>
                        <th style={{ ...cellStyle, ...th }}>Permissions</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(roleLabels).map(([key, label]) => {
                        const owned = stages.filter((s) => s.owner_role === key).map((s) => s.name);
                        const perms = roleConfig?.[key]?.permissions || {};
                        return (
                            <tr key={key}>
                                <td style={cellStyle}>
                                    <Body as="span" variant={2} weight="semibold" color="var(--color-text-secondary)" style={{
                                        display: 'inline-block', padding: '3px 10px', borderRadius: 6,
                                        background: 'var(--color-surface-muted)', border: '1px solid var(--color-border)',
                                        textTransform: 'uppercase', letterSpacing: '0.03em',
                                    }}>{label}</Body>
                                </td>
                                <td style={{ ...cellStyle, ...bodySmall({ color: 'var(--color-text-secondary)' }) }}>
                                    {owned.length ? owned.join(', ') : '—'}
                                </td>
                                <td style={cellStyle}>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {PERM_KEYS.map((pk) => {
                                            const on = !!perms[pk];
                                            const short = pk.replace('can_', '');
                                            if (!canManage) return on ? <PermTag key={pk} on>{short}</PermTag> : null;
                                            return <PermTag key={pk} on={on} onClick={() => onToggle(key, pk)}>{short}</PermTag>;
                                        })}
                                        {!canManage && Object.values(perms).every((v) => !v) && (
                                            <Body as="span" variant={2} color="var(--color-text-muted)">View only</Body>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function PermTag({ on, onClick, children }) {
    const clickable = typeof onClick === 'function';
    return (
        <button
            type="button" onClick={onClick} disabled={!clickable}
            title={clickable ? `Click to ${on ? 'revoke' : 'grant'} ${children}` : undefined}
            style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 12, fontFamily: 'var(--font-family)',
                cursor: clickable ? 'pointer' : 'default',
                border: `1px solid ${on ? 'var(--color-success)' : 'var(--color-border-strong)'}`,
                background: on ? 'var(--color-success-soft)' : 'var(--color-surface)',
                color: on ? 'var(--color-success)' : 'var(--color-text-muted)',
                fontWeight: on ? fontWeight.semibold : fontWeight.regular,
            }}>
            {on ? '✓ ' : ''}{children}
        </button>
    );
}

// ── Customisation tiles ────────────────────────────────────────────
function Tile({ dot, title, hint, children }) {
    return (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md, 10px)', background: 'var(--color-surface)', padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Body as="span" style={{ width: 9, height: 9, borderRadius: '50%', background: dot, flexShrink: 0 }} />
                <Body as="span" weight="semibold" color="var(--color-text-primary)">{title}</Body>
            </div>
            {hint && <p style={bodySmall({ color: 'var(--color-text-muted)', margin: '0 0 10px' })}>{hint}</p>}
            {children}
        </div>
    );
}

function Customisation({ settings, stages, canManage, onPatch }) {
    const th = settings.severity_thresholds || [4, 9, 15];
    const sm = settings.scale_max || 5;
    const eb = settings.email_branding || {};
    const sod = settings.sod_stages || ['review'];
    const typeRouting = settings.type_routing || {};
    const roleLabels = settings.role_labels || {};
    const stateLabels = settings.state_labels || {};
    const ratingColors = settings.rating_colors || {};
    const dis = !canManage;
    const num = (v, d) => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : d; };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 }}>
            {/* Severity thresholds */}
            <Tile dot="var(--color-critical)" title="Severity Thresholds" hint="Score boundaries: low ≤ X, medium ≤ Y, high ≤ Z, critical > Z.">
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {['Low max', 'Med max', 'High max'].map((lbl, idx) => (
                        <label key={lbl} style={bodySmall({ color: 'var(--color-text-secondary)' })}>
                            {lbl}<br />
                            <input type="number" disabled={dis} value={th[idx]}
                                onChange={(e) => { const next = [...th]; next[idx] = num(e.target.value, 0); onPatch({ severity_thresholds: next }); }}
                                style={{ ...fieldStyle, width: 70, marginTop: 4 }} />
                        </label>
                    ))}
                </div>
            </Tile>

            {/* L/I scale */}
            <Tile dot="var(--color-primary)" title="Likelihood / Impact Scale" hint="Maximum value (default 5 = ISO 5×5 matrix). Range 2–10.">
                <label style={bodySmall({ color: 'var(--color-text-secondary)' })}>
                    1 to{' '}
                    <input type="number" min="2" max="10" disabled={dis} value={sm}
                        onChange={(e) => onPatch({ scale_max: Math.max(2, Math.min(10, num(e.target.value, 5))) })}
                        style={{ ...fieldStyle, width: 70, display: 'inline-block', marginTop: 4 }} />
                </label>
            </Tile>

            {/* Share TTL */}
            <Tile dot="var(--color-info, #0891b2)" title="Share Link Lifetime" hint="How long owner-acknowledgement and share links stay valid. Default 30 days. Range 1–365.">
                <label style={bodySmall({ color: 'var(--color-text-secondary)' })}>
                    <input type="number" min="1" max="365" disabled={dis} value={settings.share_token_ttl_days ?? 30}
                        onChange={(e) => onPatch({ share_token_ttl_days: Math.max(1, Math.min(365, num(e.target.value, 30))) })}
                        style={{ ...fieldStyle, width: 90 }} /> days
                </label>
            </Tile>

            {/* Email branding */}
            <Tile dot="var(--color-success)" title="Email Branding">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={bodySmall({ color: 'var(--color-text-secondary)' })}>From address
                        <input type="email" disabled={dis} placeholder="noreply@yourdomain.com" value={eb.from_email || ''}
                            onChange={(e) => onPatch({ email_branding: { ...eb, from_email: e.target.value } })} style={{ ...fieldStyle, marginTop: 4 }} /></label>
                    <label style={bodySmall({ color: 'var(--color-text-secondary)' })}>Brand name
                        <input type="text" disabled={dis} placeholder="Your Company Risk Registry" value={eb.brand_name || ''}
                            onChange={(e) => onPatch({ email_branding: { ...eb, brand_name: e.target.value } })} style={{ ...fieldStyle, marginTop: 4 }} /></label>
                    <label style={bodySmall({ color: 'var(--color-text-secondary)' })}>Signature (optional)
                        <textarea disabled={dis} rows={2} placeholder="Appended to every email body" value={eb.signature || ''}
                            onChange={(e) => onPatch({ email_branding: { ...eb, signature: e.target.value } })} style={{ ...fieldStyle, marginTop: 4, resize: 'vertical' }} /></label>
                </div>
            </Tile>

            {/* SoD stages */}
            <Tile dot="var(--color-medium, #f59e0b)" title="SoD Enforcement Stages" hint="At these stages the actor cannot also be the registered risk owner.">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {stages.map((s) => (
                        <TogglePill key={s.id} on={sod.includes(s.id)} disabled={dis}
                            onClick={() => onPatch({ sod_stages: sod.includes(s.id) ? sod.filter((x) => x !== s.id) : [...sod, s.id] })}>
                            {s.id}
                        </TogglePill>
                    ))}
                </div>
            </Tile>

            {/* Risk type routing */}
            <Tile dot="var(--color-high, #db2777)" title="Risk Type Routing" hint="Override the stage owner for risks of a specific type. Applies at review/treatment/approve stages.">
                {Object.entries(typeRouting).map(([rt, role]) => (
                    <div key={rt} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                        <input disabled={dis} value={rt} placeholder="risk type"
                            onChange={(e) => { const next = { ...typeRouting }; delete next[rt]; if (e.target.value.trim()) next[e.target.value.trim().toLowerCase()] = role; onPatch({ type_routing: next }); }}
                            style={{ ...fieldStyle, flex: 1 }} />
                        <Body as="span" style={{ color: 'var(--color-text-muted)' }}>→</Body>
                        <CommonDropdown value={role} disabled={dis} onChange={(v) => onPatch({ type_routing: { ...typeRouting, [rt]: v } })}
                            options={ROUTABLE_ROLES.map((r) => ({ value: r, label: r }))} minWidth={130} />
                        {canManage && (
                            <button type="button" onClick={() => { const next = { ...typeRouting }; delete next[rt]; onPatch({ type_routing: next }); }}
                                style={{ ...fieldStyle, width: 'auto', padding: '5px 9px', cursor: 'pointer', background: 'var(--color-danger-soft)', color: 'var(--color-danger)', border: '1px solid var(--color-danger)' }}>×</button>
                        )}
                    </div>
                ))}
                {canManage && (
                    <CommonButton variant="secondary" text="+ Add type rule"
                        onClick={() => onPatch({ type_routing: { ...typeRouting, [`new_type_${Object.keys(typeRouting).length + 1}`]: 'risk_committee' } })} />
                )}
                {Object.keys(typeRouting).length === 0 && (
                    <p style={bodySmall({ color: 'var(--color-text-muted)', fontStyle: 'italic', margin: '8px 0 0' })}>
                        No overrides — every type uses the workflow's configured stage owner.
                    </p>
                )}
            </Tile>

            {/* Role display labels */}
            <Tile dot="var(--color-primary)" title="Role Display Labels" hint="Custom name shown in the UI. Default if blank.">
                {Object.keys(BUILTIN_ROLE_LABELS).map((rk) => (
                    <div key={rk} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                        <Body as="span" variant={2} color="var(--color-text-muted)" style={{ width: 120 }}>{rk}</Body>
                        <input type="text" disabled={dis} placeholder="(default)" value={roleLabels[rk] || ''}
                            onChange={(e) => onPatch({ role_labels: { ...roleLabels, [rk]: e.target.value } })} style={{ ...fieldStyle, flex: 1 }} />
                    </div>
                ))}
            </Tile>

            {/* Risk state labels */}
            <Tile dot="var(--color-info, #0891b2)" title="Risk State Labels" hint="Display name for each underlying state. The system value (left) stays stable for reports; only the label changes.">
                {STATE_KEYS.map((sv) => (
                    <div key={sv} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                        <Body as="span" variant={2} color="var(--color-text-muted)" style={{ width: 120, fontFamily: 'var(--font-mono, monospace)' }}>{sv}</Body>
                        <input type="text" disabled={dis} placeholder={STATE_PLACEHOLDERS[sv]} value={stateLabels[sv] || ''}
                            onChange={(e) => onPatch({ state_labels: { ...stateLabels, [sv]: e.target.value } })} style={{ ...fieldStyle, flex: 1 }} />
                    </div>
                ))}
            </Tile>

            {/* Severity colors */}
            <Tile dot="var(--color-critical)" title="Severity Colors" hint="Override the colours used for severity badges, heatmap legend and chart segments. Defaults are ISO 31000 industry-standard.">
                {['critical', 'high', 'medium', 'low'].map((sv) => {
                    const cur = ratingColors[sv] || SEVERITY_HEX_DEFAULT[sv];
                    return (
                        <div key={sv} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                            <Body as="span" variant={2} color="var(--color-text-muted)" style={{ width: 70, textTransform: 'capitalize' }}>{sv}</Body>
                            <input type="color" disabled={dis} value={/^#[0-9a-fA-F]{6}$/.test(cur) ? cur : SEVERITY_HEX_DEFAULT[sv]}
                                onChange={(e) => onPatch({ rating_colors: { ...ratingColors, [sv]: e.target.value } })}
                                style={{ width: 36, height: 30, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 0, cursor: dis ? 'default' : 'pointer' }} />
                            <input type="text" disabled={dis} value={cur}
                                onChange={(e) => { const v = e.target.value.trim(); if (/^#[0-9a-fA-F]{6}$/.test(v) || v === '') onPatch({ rating_colors: { ...ratingColors, [sv]: v || SEVERITY_HEX_DEFAULT[sv] } }); }}
                                style={{ ...fieldStyle, flex: 1, fontFamily: 'var(--font-mono, monospace)' }} />
                            <Body as="span" variant={2} color={SEVERITY_DEFAULT_COLORS[sv]} style={{ minWidth: 54, textAlign: 'right' }}>preview</Body>
                        </div>
                    );
                })}
                {canManage && (
                    <CommonButton variant="secondary" text="Reset to ISO 31000 defaults" onClick={() => onPatch({ rating_colors: {} })} />
                )}
            </Tile>
        </div>
    );
}
